import {
  COORDINATOR_LAB_TOUR_SLUGS,
  isCoordinatorLabTourSlug,
} from './coordinatorLearningLabTours.js';
import { TOUR_STRUCTURE_MANIFEST } from './tourStructure.manifest.js';

export type TourDelivery = 'platform' | 'learning_lab';

export interface TourCatalogPick {
  id: string;
  steps?: Array<{ stepName?: string }>;
}

const manifestDeliveryBySlug = new Map<string, TourDelivery>(
  TOUR_STRUCTURE_MANIFEST.tours.map((tour) => [
    tour.slug,
    tour.delivery ??
      (isCoordinatorLabTourSlug(tour.slug) ? 'learning_lab' : 'platform'),
  ]),
);

export function getTourDelivery(slug: string): TourDelivery {
  return manifestDeliveryBySlug.get(slug) ?? (isCoordinatorLabTourSlug(slug) ? 'learning_lab' : 'platform');
}

export function isLabMissionTour(id: string): boolean {
  return getTourDelivery(id) === 'learning_lab';
}

export function isPlatformOnboardingTour(flow: Pick<TourCatalogPick, 'id'>): boolean {
  return !isLabMissionTour(flow.id);
}

export function filterPlatformTours<T extends TourCatalogPick>(tours: T[]): T[] {
  return tours.filter((tour) => isPlatformOnboardingTour(tour));
}

export function filterLabMissionTours<T extends Pick<TourCatalogPick, 'id'>>(tours: T[]): T[] {
  return tours.filter((tour) => isLabMissionTour(tour.id));
}

export function countPlatformTourSteps(tours: TourCatalogPick[]): number {
  return filterPlatformTours(tours).reduce(
    (total, tour) => total + (tour.steps?.length ?? 0),
    0,
  );
}

export function collectPlatformStepNames(tours: TourCatalogPick[]): Set<string> {
  const names = new Set<string>();
  for (const tour of filterPlatformTours(tours)) {
    for (const step of tour.steps ?? []) {
      if (step.stepName) {
        names.add(step.stepName);
      }
    }
  }
  return names;
}

export function countPlatformCompletedSteps(
  completedSteps: string[],
  platformTours: TourCatalogPick[],
): number {
  const platformStepNames = collectPlatformStepNames(platformTours);
  return completedSteps.filter((step) => platformStepNames.has(step)).length;
}

export function computePlatformCompletionPercentage(
  completedSteps: string[],
  platformTours: TourCatalogPick[],
): number {
  const totalSteps = countPlatformTourSteps(platformTours);
  if (totalSteps <= 0) {
    return 0;
  }
  const completedCount = countPlatformCompletedSteps(completedSteps, platformTours);
  return Math.min(100, Math.max(0, Math.floor((completedCount / totalSteps) * 100)));
}

/** Slugs counted as lab missions (for tests and manifest sync). */
export function getLabMissionTourSlugs(): readonly string[] {
  return COORDINATOR_LAB_TOUR_SLUGS;
}

/** Manifest platform tours for server-side progress fallback (client uses expanded steps). */
export function getPlatformManifestTours(): TourCatalogPick[] {
  return TOUR_STRUCTURE_MANIFEST.tours
    .filter((tour) => !isLabMissionTour(tour.slug))
    .map((tour) => ({
      id: tour.slug,
      steps: tour.steps.map((step) => ({ stepName: step.stepName })),
    }));
}
